import { UzAvatar, UzButton, UzCard, UzInput, UzSpinner } from '@utazzo/ui';
import { KeyRound, Save } from 'lucide-react';
import React, { useEffect, useState } from 'react';

import { ActiveSessionsCard } from '../components/ActiveSessionsCard';
import { ChangePasswordDialog } from '../components/ChangePasswordDialog';
import { useMyProfile, useUpdateMyProfile } from '../use-my-profile';

import { PageShell } from '@/components/page-shell';
import { PushSettingsCard } from '@/features/push/components/PushSettingsCard';

/**
 * My Profile (ADR-0040). Self-service surface for the signed-in
 * operator: read-only account info + editable name/phone + password
 * change.
 */
export const MyProfilePage = (): React.JSX.Element => {
  const profile = useMyProfile();
  const update = useUpdateMyProfile();
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [passwordOpen, setPasswordOpen] = useState(false);

  // Hydrate the edit form when the profile loads. We DON'T reset on
  // every profile refetch because the operator might be mid-edit; only
  // initialize from undefined.
  const dataKey = profile.data?.user.id;
  useEffect(() => {
    if (!profile.data) return;
    setFullName(profile.data.user.fullName);
    setPhone(profile.data.user.phone ?? '');
    // intentionally only re-hydrate when the user id changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataKey]);

  const dirty = Boolean(
    profile.data &&
    (fullName !== profile.data.user.fullName || (phone || null) !== profile.data.user.phone),
  );
  const onSave = async (): Promise<void> => {
    if (!dirty) return;
    await update.mutateAsync({ fullName, phone: phone.trim() ? phone.trim() : null });
  };

  if (profile.isLoading || !profile.data) {
    return (
      <PageShell title="My profile" description="">
        <UzCard>
          <div className="flex items-center gap-2 p-6 text-sm text-zinc-500">
            <UzSpinner size="sm" /> Loading profile...
          </div>
        </UzCard>
      </PageShell>
    );
  }

  const { user, activitySummary } = profile.data;

  return (
    <PageShell
      title="My profile"
      description="Your account. Email, role, branch + department are admin-managed - ask an admin if any of those need a change."
    >
      <div className="mx-auto max-w-4xl space-y-4">
        <UzCard
          title="Account"
          actions={
            <UzButton onClick={onSave} loading={update.isPending} disabled={!dirty}>
              <Save className="mr-1.5 h-4 w-4" />
              Save changes
            </UzButton>
          }
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
            <UzAvatar name={user.fullName} size="lg" />
            <div className="grid flex-1 grid-cols-1 gap-3 sm:grid-cols-2">
              <UzInput
                name="fullName"
                label="Full name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                disabled={update.isPending}
              />
              <UzInput
                name="phone"
                label="Phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                disabled={update.isPending}
              />
              <UzInput name="email" label="Email" value={user.email} readOnly disabled />
              <UzInput name="role" label="Role" value={user.role} readOnly disabled />
              <UzInput
                name="branch"
                label="Branch"
                value={user.branch?.name ?? '-'}
                readOnly
                disabled
              />
              <UzInput
                name="department"
                label="Department"
                value={user.department?.name ?? '-'}
                readOnly
                disabled
              />
              <UzInput
                name="employeeCode"
                label="Employee code"
                value={user.employeeCode ?? '-'}
                readOnly
                disabled
              />
              <UzInput
                name="lastLogin"
                label="Last login"
                value={user.lastLogin ? new Date(user.lastLogin).toLocaleString('en-GB') : '-'}
                readOnly
                disabled
              />
            </div>
          </div>
        </UzCard>

        <UzCard
          title="Security"
          description="Change your password whenever you want. We don't email a confirmation - the change is immediate."
          actions={
            <UzButton variant="outline" onClick={() => setPasswordOpen(true)}>
              <KeyRound className="mr-1.5 h-4 w-4" />
              Change password
            </UzButton>
          }
        >
          <p className="text-sm text-zinc-500">
            Joined{' '}
            <strong className="text-zinc-700">
              {new Date(user.createdAt).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })}
            </strong>
            . Last login was{' '}
            <strong className="text-zinc-700">
              {user.lastLogin
                ? new Date(user.lastLogin).toLocaleString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : 'never recorded'}
            </strong>
            .
          </p>
        </UzCard>

        <PushSettingsCard />

        <ActiveSessionsCard />

        <UzCard title="Today">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            <Counter label="Leads in your queue" value={activitySummary.leadsOwned} />
            <Counter label="Follow-ups due today" value={activitySummary.followUpsDueToday} />
            <Counter
              label="Follow-ups done this week"
              value={activitySummary.followUpsCompletedThisWeek}
            />
          </div>
        </UzCard>
      </div>

      <ChangePasswordDialog open={passwordOpen} onOpenChange={setPasswordOpen} />
    </PageShell>
  );
};

const Counter = ({ label, value }: { label: string; value: number }): React.JSX.Element => (
  <div className="rounded-md border border-zinc-200 bg-zinc-50/60 px-3 py-2">
    <div className="text-2xl font-semibold text-zinc-900">{value.toLocaleString('en-IN')}</div>
    <div className="text-xs text-zinc-600">{label}</div>
  </div>
);
